
"use client";

import { useState } from 'react';
import { diagnoseCarProblems, type DiagnoseCarProblemsOutput } from '@/ai/flows/diagnose-car-problems';
import { lookupVin, type VinLookupOutput } from '@/ai/flows/vin-lookup';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, MessageSquare, Search, Car, History, ShieldCheck, Save, Lightbulb, Info, Globe, Fuel, Gauge, Settings } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { getContextualTips } from '@/app/lib/tips-data';

export default function DiagnoseClient() {
  const { toast } = useToast();
  const [symptoms, setSymptoms] = useState('');
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diagResult, setDiagResult] = useState<DiagnoseCarProblemsOutput | null>(null);
  const [vinResult, setVinResult] = useState<VinLookupOutput | null>(null);
  
  // State for inline advice
  const [localAdvice, setLocalAdvice] = useState<string | null>(null);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);

  const handleDiagnose = async () => {
    if (!symptoms.trim()) return;
    setLoading(true);
    try {
      const output = await diagnoseCarProblems({ symptoms });
      setDiagResult(output);
      setVinResult(null);
    } catch (error) {
      console.error(error);
      toast({ variant: 'destructive', title: 'خطأ', description: 'فشل التشخيص، يرجى المحاولة لاحقاً.' });
    } finally {
      setLoading(false);
    }
  };

  const handleVinLookup = async () => {
    if (!vin.trim()) return;
    setLoading(true);
    try {
      const output = await lookupVin({ vin });
      setVinResult(output);
      setDiagResult(null);
    } catch (error) {
      console.error(error);
      toast({ variant: 'destructive', title: 'خطأ', description: 'فشل جلب بيانات VIN.' });
    } finally {
      setLoading(false);
    }
  };

  const generateAdvice = () => {
    setGeneratingAdvice(true);
    // Simulate generation delay
    setTimeout(() => {
      const tips = getContextualTips('inspection', 1);
      setLocalAdvice(tips[0].text_ar);
      setGeneratingAdvice(false);
    }, 800);
  };

  const handleSaveReport = async () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast({
        title: "تم الحفظ",
        description: "تم حفظ التقرير في كراجك الخاص بنجاح.",
      });
    }, 1000);
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto pb-20">
      <Tabs defaultValue="symptoms" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-14 bg-white rounded-2xl shadow-sm p-1">
          <TabsTrigger value="symptoms" className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white font-bold">
            <MessageSquare className="h-4 w-4 ml-2" />
            تشخيص الأعراض
          </TabsTrigger>
          <TabsTrigger value="vin" className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white font-bold">
            <Search className="h-4 w-4 ml-2" />
            البحث برقم VIN
          </TabsTrigger>
        </TabsList>

        <TabsContent value="symptoms" className="mt-6">
          <Card className="border-none shadow-xl rounded-[2.5rem] overflow-hidden bg-white">
            <CardHeader className="bg-primary text-white p-8 pb-10">
              <div className="flex items-center gap-4 mb-2">
                <div className="bg-white/20 p-3 rounded-2xl">
                   <Sparkles className="h-7 w-7" />
                </div>
                <CardTitle className="text-xl font-black font-headline tracking-tight">التشخيص النصي الذكي</CardTitle>
              </div>
              <p className="text-white/70 text-sm font-medium">صف الأصوات أو الأعراض بدقة وسيقوم الخبير الآلي بتحليلها فوراً.</p>
            </CardHeader>
            <CardContent className="p-8 -mt-6">
              <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-50">
                <Textarea 
                  placeholder="مثال: أسمع صوت صرير حاد عند الضغط على الفرامل..."
                  className="min-h-[160px] rounded-2xl bg-slate-50 border-none focus:ring-primary mb-6 text-sm leading-relaxed p-4"
                  value={symptoms}
                  onChange={(e) => setSymptoms(e.target.value)}
                />
                <Button 
                  className="w-full h-16 rounded-[2rem] text-lg font-black bg-slate-900 hover:bg-slate-800 text-white shadow-xl transition-all"
                  onClick={handleDiagnose}
                  disabled={loading || !symptoms.trim()}
                >
                  {loading ? <Loader2 className="animate-spin h-6 w-6" /> : "ابدأ الفحص الفوري"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vin" className="mt-6">
          <Card className="border-none shadow-xl rounded-[2.5rem] overflow-hidden bg-white">
            <CardHeader className="bg-secondary text-white p-8 pb-10">
              <div className="flex items-center gap-4 mb-2">
                <div className="bg-white/20 p-3 rounded-2xl">
                   <ShieldCheck className="h-7 w-7" />
                </div>
                <CardTitle className="text-xl font-black font-headline tracking-tight">تقرير حالة السيارة (VIN)</CardTitle>
              </div>
              <p className="text-white/70 text-sm font-medium">أدخل رقم الهيكل المكون من 17 رمزاً لجلب المواصفات وتاريخ الحوادث.</p>
            </CardHeader>
            <CardContent className="p-8 -mt-6">
              <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-50">
                <Input 
                  placeholder="أدخل رقم الهيكل (VIN)..."
                  maxLength={17}
                  className="h-14 rounded-2xl bg-slate-50 border-none focus:ring-secondary mb-6 text-center font-mono text-lg tracking-widest uppercase"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                />
                <Button 
                  className="w-full h-16 rounded-[2rem] text-lg font-black bg-secondary hover:bg-secondary/90 text-white shadow-xl transition-all"
                  onClick={handleVinLookup}
                  disabled={loading || vin.length < 10}
                >
                  {loading ? <Loader2 className="animate-spin h-6 w-6" /> : "جلب تقرير السيارة"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Interactive Advice Button */}
      <div className="space-y-4">
        <Button 
          variant="outline"
          className="w-full h-14 rounded-2xl border-dashed border-primary text-primary flex gap-2 font-bold hover:bg-blue-50 bg-white"
          onClick={generateAdvice}
          disabled={generatingAdvice}
        >
          {generatingAdvice ? <Loader2 className="animate-spin h-4 w-4" /> : <Lightbulb className="h-4 w-4" />}
          بماذا تنصحني؟
        </Button>

        {localAdvice && (
          <div className="p-5 bg-indigo-50 border border-indigo-100 rounded-[2rem] animate-in fade-in slide-in-from-top-2 duration-500">
            <div className="flex items-center gap-2 mb-2 text-primary">
              <Sparkles className="h-4 w-4" />
              <h4 className="text-xs font-black uppercase">نصيحة الخبير المخصصة:</h4>
            </div>
            <p className="text-sm font-bold text-slate-700 leading-relaxed">{localAdvice}</p>
            <div className="mt-3 pt-3 border-t border-indigo-100 flex items-center gap-2">
              <Info className="h-3 w-3 text-indigo-400" />
              <p className="text-[10px] text-indigo-400 font-bold uppercase tracking-tight">مقارنة: تقارير الفحص الجاهزة تعزز الثقة بنسبة 40%.</p>
            </div>
          </div>
        )}
      </div>

      {(diagResult || vinResult) && (
        <Button 
          variant="outline"
          className="w-full h-14 rounded-2xl border-dashed border-primary text-primary flex gap-2 font-bold hover:bg-blue-50"
          onClick={handleSaveReport}
          disabled={saving}
        >
          {saving ? <Loader2 className="animate-spin h-4 w-4" /> : <Save className="h-4 w-4" />}
          حفظ هذا التقرير في كراجي الخاص
        </Button>
      )}

      {/* نتائج التشخيص النصي */}
      {diagResult && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="border-none shadow-md rounded-[2.5rem] bg-indigo-50/50 p-2">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 text-primary">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="text-lg font-black">التشخيص الأولي</h3>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed text-sm font-medium">{diagResult.diagnosis}</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md rounded-[2.5rem] bg-emerald-50/50 p-2">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-5 w-5" />
                <h3 className="text-lg font-black">الحلول المقترحة</h3>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed text-sm font-medium">{diagResult.potentialSolutions}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* نتائج البحث برقم VIN */}
      {vinResult && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="border-none shadow-md rounded-[2.5rem] bg-white overflow-hidden">
             <div className="bg-slate-900 p-4 text-white flex justify-between items-center px-8">
               <h3 className="font-bold flex items-center gap-2"><Car className="h-5 w-5" /> مواصفات السيارة</h3>
               <div className="flex items-center gap-2">
                 <Badge className="bg-emerald-500 flex items-center gap-1 text-[10px]">
                   <Globe className="h-3 w-3" /> بيانات حقيقية
                 </Badge>
                 <Badge className="bg-secondary">{vinResult.carDetails.country}</Badge>
               </div>
             </div>
             <CardContent className="p-6 grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">الماركة والموديل</p>
                  <p className="text-sm font-black">{vinResult.carDetails.make} {vinResult.carDetails.model}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">سنة الصنع</p>
                  <p className="text-sm font-black">{vinResult.carDetails.year}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">المحرك</p>
                  <p className="text-sm font-black">{vinResult.carDetails.engine}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">الفئة</p>
                  <p className="text-sm font-black">{vinResult.carDetails.trim || 'Standard'}</p>
                </div>
                {vinResult.carDetails.bodyClass && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-400 font-bold uppercase">نوع الهيكل</p>
                    <p className="text-sm font-black">{vinResult.carDetails.bodyClass}</p>
                  </div>
                )}
                {vinResult.carDetails.fuelType && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-400 font-bold uppercase">الوقود</p>
                    <p className="text-sm font-black">{vinResult.carDetails.fuelType}</p>
                  </div>
                )}
                {vinResult.carDetails.driveType && (
                  <div className="space-y-1 col-span-2">
                    <p className="text-[10px] text-slate-400 font-bold uppercase">نوع الدفع</p>
                    <p className="text-sm font-black">{vinResult.carDetails.driveType}</p>
                  </div>
                )}
             </CardContent>
             {vinResult.dataSource && (
               <div className="px-6 pb-4">
                 <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                   <Info className="h-3 w-3 text-slate-400 shrink-0" />
                   <p className="text-[10px] text-slate-400 font-bold">المصدر: {vinResult.dataSource}</p>
                 </div>
               </div>
             )}
          </Card>

          <Card className="border-none shadow-md rounded-[2.5rem] bg-white overflow-hidden">
             <div className="bg-orange-500 p-4 text-white px-8 flex items-center gap-2">
               <History className="h-5 w-5" />
               <h3 className="font-bold">سجل الحوادث والتقارير</h3>
             </div>
             <CardContent className="p-6">
                {vinResult.accidentHistory.length === 0 ? (
                  <div className="py-6 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    <p className="font-bold text-slate-600">السجل نظيف! لم يتم العثور على حوادث مسجلة.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {vinResult.accidentHistory.map((acc, i) => (
                      <div key={i} className="flex gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                        <div className={`w-2 h-auto rounded-full ${
                          acc.severity === 'critical' ? 'bg-red-500' : 
                          acc.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                        }`} />
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <h4 className="font-bold text-sm">{acc.description_ar}</h4>
                            <span className="text-[10px] text-slate-400">{acc.date}</span>
                          </div>
                          <p className="text-xs text-slate-500">{acc.location_ar}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                   <p className="text-xs font-medium text-blue-800 leading-relaxed">
                     <span className="font-black">ملخص التقرير: </span> {vinResult.summary_ar}
                   </p>
                </div>
             </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
